import { motion, useReducedMotion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer, 
  viewportOnce,
  viewportOnceNear,
  ease,
  systemDiagramContainer,
  systemCenterNode,
  systemLabelsContainer,
  systemLabelItem
} from "@/lib/motion";

export interface GrowthSystemContent {
  eyebrow: string;
  headline: string;
  description: string;
  contextNote: string;
  feedbackNote: string;
  steps: {
    phase: string;
    label: string;
    description: string;
    detail: string;
  }[];
}

export const growthSystemDefaults: GrowthSystemContent = {
  eyebrow: "The System",
  headline: "Growth that compounds.",
  description: "Most companies optimize tactics in isolation. We build interconnected systems where each improvement amplifies the next.",
  contextNote: "Teams typically engage when growth has stalled despite increased effort. Or when channel economics no longer compound.",
  feedbackNote: "Each stage feeds the next. Improvements compound.",
  steps: [
    { phase: "01", label: "Visibility", description: "Get found by the right audience", detail: "Search, social, content. Precision over reach." },
    { phase: "02", label: "Acquisition", description: "Convert attention into interest", detail: "Paid, organic, referral. Systematic, not scattered." },
    { phase: "03", label: "Conversion", description: "Turn interest into customers", detail: "Landing, flow, offer. Clarity beats persuasion." },
    { phase: "04", label: "Retention", description: "Grow revenue from existing customers", detail: "Email, lifecycle, loyalty. Compound, not churn." },
  ],
};

interface GrowthSystemOverviewProps {
  content?: GrowthSystemContent;
}

const GrowthSystemOverview = ({ content: c = growthSystemDefaults }: GrowthSystemOverviewProps) => {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <section className="py-12 md:py-16 lg:py-20 relative overflow-hidden">
      {/* Ultra-light grid texture */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(216 30% 50%) 1px, transparent 1px),
            linear-gradient(90deg, hsl(216 30% 50%) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />
      
      {/* Gradient mesh */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 120% 60% at 50% 0%, hsl(216 99% 45% / 0.06) 0%, transparent 50%),
            radial-gradient(ellipse 100% 80% at 100% 80%, hsl(169 66% 52% / 0.04) 0%, transparent 40%),
            radial-gradient(ellipse 80% 60% at 0% 60%, hsl(216 80% 55% / 0.03) 0%, transparent 40%)
          `,
        }}
      />
      
      {/* Directional light glow */}
      <motion.div 
        className="absolute -top-24 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: ease }}
        style={{
          background: 'radial-gradient(ellipse, hsl(216 99% 60% / 0.05) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />
      
      {/* Subtle flow line */}
      <div 
        className="absolute top-1/2 left-0 right-0 h-px pointer-events-none opacity-[0.04]"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, hsl(216 99% 50%) 20%, hsl(216 99% 50%) 80%, transparent 100%)',
        }}
      />
      
      <div className="container-wide relative z-10">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-8 md:mb-12 lg:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.div variants={sectionVariant} className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {c.eyebrow}
            </span>
            <div className="w-8 h-px bg-primary" />
          </motion.div>
          <motion.h2 variants={sectionVariant} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
            {c.headline}
          </motion.h2>
          <motion.p variants={sectionVariant} className="text-muted-foreground leading-relaxed text-lg max-w-2xl mx-auto mb-3">
            {c.description}
          </motion.p>
          <motion.p variants={sectionVariant} className="text-xs text-muted-foreground/50 max-w-xl mx-auto">
            {c.contextNote}
          </motion.p>
        </motion.div>

        <motion.div 
          className="max-w-5xl mx-auto"
          initial={shouldReduceMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={systemDiagramContainer}
        >
          {/* Desktop horizontal flow */}
          <div className="hidden md:block">
            <div className="relative">
              <motion.div 
                className="absolute top-16 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent origin-left"
                initial={shouldReduceMotion ? { scaleX: 1 } : { scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={viewportOnceNear}
                transition={{ duration: 0.8, delay: 0.3, ease: ease }}
              />
              
              <motion.div 
                className="grid grid-cols-4 gap-8"
                initial={shouldReduceMotion ? "visible" : "hidden"}
                whileInView="visible"
                viewport={viewportOnceNear}
                variants={systemLabelsContainer}
              >
                {c.steps.map((step, index) => (
                  <motion.div 
                    key={step.phase} 
                    className="relative text-center"
                    variants={systemLabelItem}
                  >
                    <motion.div className="relative z-10 mb-8" variants={systemCenterNode}>
                      <div className="w-12 h-12 mx-auto rounded-full bg-background border-2 border-primary/20 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">{step.phase}</span>
                      </div>
                    </motion.div>
                    <h3 className="text-lg font-display font-medium text-foreground mb-2">{step.label}</h3>
                    <p className="text-sm text-foreground/80 mb-2">{step.description}</p>
                    <p className="text-xs text-muted-foreground">{step.detail}</p>
                    {index < c.steps.length - 1 && (
                      <div className="absolute top-4 -right-4 w-8 flex items-center justify-center">
                        <svg className="w-4 h-4 text-primary/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </motion.div>
                ))}
              </motion.div>
              
              <motion.div 
                className="mt-12 flex items-center justify-center"
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={viewportOnceNear}
                transition={{ delay: 0.8, duration: 0.4, ease: ease }}
              >
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{c.feedbackNote}</span>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Mobile vertical flow */}
          <motion.div 
            className="md:hidden space-y-8"
            initial={shouldReduceMotion ? "visible" : "hidden"}
            whileInView="visible"
            viewport={viewportOnceNear}
            variants={systemLabelsContainer}
          >
            {c.steps.map((step, index) => (
              <motion.div key={step.phase} className="relative flex gap-6" variants={systemLabelItem}>
                {index < c.steps.length - 1 && (
                  <div className="absolute left-5 top-12 bottom-0 w-px bg-gradient-to-b from-primary/30 to-transparent" />
                )}
                <div className="relative z-10 shrink-0">
                  <div className="w-10 h-10 rounded-full bg-background border-2 border-primary/20 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">{step.phase}</span>
                  </div>
                </div>
                <div className="pt-1 pb-6">
                  <h3 className="text-base font-display font-medium text-foreground mb-1">{step.label}</h3>
                  <p className="text-sm text-foreground/80 mb-1">{step.description}</p>
                  <p className="text-xs text-muted-foreground">{step.detail}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default GrowthSystemOverview;
